import React, { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { IFormAction } from '../../@types/entityTypes';
import { FetchResult, useMutation, useQuery } from '@apollo/client';
import EditableDataGridViewsQuery from '../../graphql/queries/view/EditableDataGridViewsQuery.graphql';
import EditableDataGridViewMutation from '../../graphql/mutations/view/EditableDataGridViewMutation.graphql';
import {
  AlertType,
  BooleanOperator,
  Direction,
  FilterOperation,
  IEditableDataGridColumn,
  IEditableDataGridView,
  IEditableDataGridViewInput,
  IEditableDataGridViewMutationResult,
  IEditableDataGridViewMutationVariables,
  IEditableDataGridViewsQueryResult,
  IEditableDataGridViewsQueryVariables,
  IFilterInput,
} from '../../graphql/schema/p4types';
import { ContextualMenuItemType } from '@fluentui/react';
import { BasicHeader } from '../../actions/BasicHeader';
import { PrimaryButton, SecondaryButton } from '../connectedForm/Button';
import {
  Button,
  createTableColumn,
  Field,
  FluentProvider,
  Input,
  makeStyles,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableRowId,
  Tooltip,
  useFocusableGroup,
  webLightTheme,
} from '@fluentui/react-components';
import { useFullUserDetails, useValueState } from '../../utils/hooks';
import getClientMutationId from '../../utils/getClientMutationId';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { cloneDeep } from '@apollo/client/utilities';
import * as UuIdUtils from '../../utils/UuIdGenerator';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { InfoTheme } from '../../styles/themes/default/defaultFluentTheme';
import { FluentTable, IRowActionCells } from './FluentViewTable';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { DeleteRegular, PeopleRegular, PersonRegular } from '@fluentui/react-icons';
import RequiredIcon from '../connectedForm/RequiredIcon';
import FluentModal from '../modals/FluentModal';

export enum IEditableDataGridAction {
  ManageViews = 'MANAGE_VIEWS',
}

type IEditableDataGridViewControllerProps = {
  viewScreenName: string;
  columns: Array<IEditableDataGridColumnDef>;
  actionProps?: {
    //Whether the returned action button should be shown as a standalone, default is true.
    individualButton?: boolean;
    text?: string;
  };
};

const VIEW_TABLE_COLUMNS: TableColumnDefinition<IEditableDataGridView>[] = [
  createTableColumn<IEditableDataGridView>({
    columnId: 'name',
    renderCell: (item) => <span>{item.name}</span>,
  }),
];

const COLUMN_TABLE_COLUMNS: TableColumnDefinition<IEditableDataGridColumnDef>[] = [
  createTableColumn<IEditableDataGridColumnDef>({
    columnId: 'name',
    renderCell: (item) => {
      return (
        <span>
          {item.name} <RequiredIcon mandatory={item.viewControllerMandatory} />
        </span>
      );
    },
  }),
  createTableColumn<IEditableDataGridColumnDef>({
    columnId: 'width',
    renderCell: (item) => <span>{item.width ?? 'Default'}</span>,
  }),
];

const useStyles = makeStyles({
  field: {
    flexGrow: 0.2,
  },
});

const getDefaultColumns = (columns: Array<IEditableDataGridColumnDef>): Array<IEditableDataGridColumn> => {
  return columns.map(
    (column) =>
      ({
        uuid: UuIdUtils.uuIdGenerator(),
        fieldName: column.key as string,
        name: column.name,
        width: column.width,
        active: !column.viewControllerOptIn,
      } as IEditableDataGridColumn)
  );
};

export type IEditableDataGridViewController = {
  actions: Array<IFormAction<IEditableDataGridAction>>;
  modals: ReactElement;
  columns: Array<IEditableDataGridColumnDef>;
  handleColumnResize: (fieldName: string, width: number) => void;
};

const useActionCellStyles = makeStyles({
  actionCell: {
    maxWidth: '45px',
  },
});

const useActionCell = ({
  onClickAction,
  hint,
  icon,
  condition,
  ariaLabel,
}: {
  onClickAction: (ev: any, row: any) => void;
  hint: (row: any) => string;
  icon: ReactNode | ((row: any) => ReactNode);
  condition?: (row: any) => boolean;
  ariaLabel?: string;
}) => {
  const styles = useActionCellStyles();
  const focusableGroupAttr = useFocusableGroup({
    tabBehavior: 'limited-trap-focus',
  });

  return useCallback(
    ({ item, rowIndex }) => {
      const disabled = condition && !condition(item);
      const content = (
        <Button
          icon={typeof icon === 'function' ? icon(item) : icon}
          aria-label={ariaLabel}
          onClick={(ev) => onClickAction(ev, item)}
          disabled={disabled}
        />
      );

      return (
        <TableCell role="gridcell" tabIndex={0} {...focusableGroupAttr} className={styles.actionCell}>
          <TableCellLayout>
            {disabled ? (
              content
            ) : (
              <Tooltip content={hint?.(item)} relationship={'label'}>
                {content}
              </Tooltip>
            )}
          </TableCellLayout>
        </TableCell>
      );
    },
    [condition, focusableGroupAttr, hint, icon, onClickAction, styles.actionCell]
  );
};

const useEditableDataGridViewController = ({
  viewScreenName,
  columns,
  actionProps,
}: IEditableDataGridViewControllerProps): IEditableDataGridViewController => {
  const [viewControllerOpen, setViewControllerOpen] = useState(false);
  const [selectedView, setSelectedView] = useValueState<IEditableDataGridView>({
    columns: getDefaultColumns(columns),
  } as IEditableDataGridView);
  const selectedColumns = useMemo(() => {
    return new Set(
      selectedView.columns?.filter((value) => value?.active)?.map((value) => value!.fieldName as TableRowId) ?? []
    );
  }, [selectedView.columns]);
  const selectedViews = useMemo(() => new Set([selectedView?.uuid as TableRowId]), [selectedView]);
  const { dispatchSimpleToast } = useP4ToastController();
  const fullUserDetails = useFullUserDetails();

  const predefinedFilters = useMemo<Array<IFilterInput>>(
    () => [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'screenName',
          value: viewScreenName,
        },
      },
      {
        filterOperation: FilterOperation.Custom, //TODO - Use '.None' for container - Not currently available in master.
        filterParameters: {
          booleanOperator: BooleanOperator.Or,
          filters: [
            {
              filterOperation: FilterOperation.Equals,
              filterParameters: {
                field: 'privateView',
                value: 'false',
              },
            },
            {
              filterOperation: FilterOperation.Equals,
              filterParameters: {
                field: 'createdBy',
                value: fullUserDetails?.systemUser?.username,
              },
            },
          ],
        },
      },
    ],
    [fullUserDetails?.systemUser?.username, viewScreenName]
  );

  const { data: viewData } = useQuery<IEditableDataGridViewsQueryResult, IEditableDataGridViewsQueryVariables>(
    EditableDataGridViewsQuery,
    {
      variables: {
        filters: predefinedFilters,
        orderBy: 'updatedAt',
        orderDirection: Direction.Desc,
      },
      onCompleted: (data) => {
        const view = data.editableDataGridViews?.edges?.[0]?.node;
        if (view) {
          setSelectedView(view as IEditableDataGridView);
        }
      },
    }
  );

  const handleQueryError = useComponentQueryErrorHandling();
  const [updateView] = useMutation<IEditableDataGridViewMutationResult, IEditableDataGridViewMutationVariables>(
    EditableDataGridViewMutation,
    {
      onError: handleQueryError,
    }
  );

  //Wrap the view into an action for the moment - Neatly fits into a form, or we can easily provide to a Fluent-UI button.
  const action: IFormAction<IEditableDataGridAction> = useMemo(() => {
    return {
      key: 'viewController',
      text: actionProps?.text ?? 'Manage Views',
      onClick: () => {
        setViewControllerOpen(true);
      },
      individualButton: actionProps?.individualButton ?? true,
      actionReference: IEditableDataGridAction.ManageViews,
      buttonProps: {
        iconProps: { iconName: 'taskManager' },
        theme: InfoTheme,
        style: { order: 2 }, //'Actions' dropdown is '3', we want to be next to that for view manager.
      },
    };
  }, [actionProps?.individualButton, actionProps?.text]);

  const items: Array<IEditableDataGridView> = useMemo(
    () =>
      (viewData?.editableDataGridViews?.edges
        ?.map((value) => value?.node)
        .filter((node) => !!node?.uuid) as Array<IEditableDataGridView>) ?? [],
    [viewData?.editableDataGridViews?.edges]
  );

  const mappedColumns = useMemo(() => {
    if (!selectedView) return columns;

    return columns.map((column) => {
      const viewColumn = selectedView.columns?.find((selectedColumn) => selectedColumn?.fieldName === column.key);
      if (!viewColumn) return column;
      return { ...column, width: viewColumn.width ?? column.width };
    });
  }, [columns, selectedView]);

  const filteredColumns = useMemo(() => {
    if (!selectedView?.id) {
      //View not yet saved, show all except 'opt-in' columns.
      return mappedColumns.filter((column) => !column.viewControllerOptIn);
    }
    return mappedColumns.filter((value) => {
      return selectedColumns.has(value.key as TableRowId);
    });
  }, [mappedColumns, selectedColumns, selectedView?.id]);

  const onClickSave = useCallback(() => {
    const newView = normalizeVariables(selectedView) as IEditableDataGridViewInput;
    if (!newView.id) {
      //If the view name was changed, we need to reset id/uuid/concurrencyVersion or the new view will "steal" the old views columns.
      newView.columns?.forEach((column) => {
        delete column!.id;
        delete column!.concurrencyVersion;
        column!.uuid = UuIdUtils.uuIdGenerator();
      });
    }

    updateView({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          editableDataGridView: {
            ...newView,
            screenName: viewScreenName,
          },
        },
      },
      refetchQueries: [EditableDataGridViewsQuery],
    }).then((result) => {
      postViewSave(result);
    });
  }, [selectedView, updateView, viewScreenName, dispatchSimpleToast, setSelectedView]);

  const postViewSave = useCallback(
    (result: FetchResult<IEditableDataGridViewMutationResult>) => {
      const viewName = result.data?.editableDataGridViewUpdate?.result?.name;
      if (viewName) {
        dispatchSimpleToast('Saved', `View ${viewName} has been saved.`, AlertType.Success);
        setSelectedView(result.data?.editableDataGridViewUpdate!.result as IEditableDataGridView);
      } else {
        dispatchSimpleToast('Save Unsuccessful', 'View could not be saved.', AlertType.Error);
      }
    },
    [dispatchSimpleToast, setSelectedView]
  );

  const onClickDelete = useCallback(
    (ev, row) => {
      updateView({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            editableDataGridView: {
              ...(normalizeVariables(row) as IEditableDataGridViewInput),
              deleted: true,
            },
          },
        },
        refetchQueries: [EditableDataGridViewsQuery],
      });
    },
    [updateView]
  );

  const handleColumnResize: (fieldName: string, width: number) => void = useCallback(
    (fieldName, width) => {
      setSelectedView((prevState) => {
        const newView = cloneDeep(prevState);
        const column = newView.columns?.find((col) => col?.fieldName === fieldName);
        if (column) {
          column.width = Math.floor(width);
          return newView;
        }
        return prevState;
      });
    },
    [setSelectedView]
  );

  const viewTableFeatureOptions = useMemo(
    () => ({
      columns: VIEW_TABLE_COLUMNS,
      items,
    }),
    [items]
  );

  const columnTableFeatureOptions = useMemo(
    () => ({
      columns: COLUMN_TABLE_COLUMNS,
      items: mappedColumns,
    }),
    [mappedColumns]
  );

  const styles = useStyles();

  const deleteCells = useActionCell({
    onClickAction: onClickDelete,
    hint: () => 'Delete this view',
    icon: <DeleteRegular />,
    condition: (row: IEditableDataGridView) => {
      return fullUserDetails?.systemUser?.username === row.createdBy || !!fullUserDetails?.systemUser?.admin;
    },
    ariaLabel: 'Delete',
  });

  const onClickPrivate = useCallback(
    (ev, row) => {
      updateView({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            editableDataGridView: {
              ...(normalizeVariables(row) as IEditableDataGridViewInput),
              privateView: !row.privateView,
            },
          },
        },
        refetchQueries: [EditableDataGridViewsQuery],
      }).then((result) => {
        postViewSave(result);
      });
    },
    [updateView]
  );

  const privateCells = useActionCell({
    onClickAction: onClickPrivate,
    hint: (row: IEditableDataGridView) =>
      row.privateView ? 'Private: Click to make this view public' : 'Public: Click to make this view private',
    icon: (row: IEditableDataGridView) => {
      return row.privateView ? <PersonRegular /> : <PeopleRegular />;
    },
    condition: (row: IEditableDataGridView) => {
      return fullUserDetails?.systemUser?.username === row.createdBy;
    },
    ariaLabel: 'Private',
  });

  const rowActionCells = useCallback<IRowActionCells>(
    ({ item, rowIndex }) => {
      return (
        <>
          {privateCells({ item, rowIndex })}
          {deleteCells({ item, rowIndex })}
        </>
      );
    },
    [privateCells, deleteCells]
  );

  return useMemo(
    () => ({
      actions:
        actionProps?.individualButton ?? true
          ? [action]
          : [
              {
                key: 'viewsHeader',
                itemType: ContextualMenuItemType.Header,
                text: 'Views',
                actionReference: '-',
                ignorePermissions: true,
              } as unknown as IFormAction<IEditableDataGridAction>, //Coerce the type, special case (Items separator).
              action,
            ],
      modals: (
        <FluentModal isOpen={viewControllerOpen}>
          <FluentProvider theme={webLightTheme}>
            <div style={{ width: '1000px' }}>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div style={{ width: '40%' }}>
                  <BasicHeader>View Controller</BasicHeader>
                  <FluentTable<IEditableDataGridView>
                    itemCount={viewData?.editableDataGridViews?.count ?? 0}
                    height={300}
                    tableFeatureOptions={viewTableFeatureOptions}
                    selectionMode={'single'}
                    selectionKey={'uuid'}
                    onSelectionChange={(e, data) => {
                      const selectedUuid = data.selectedItems.values()?.next()?.value;
                      const selectedView = items.find((value) => value.uuid === selectedUuid);
                      if (selectedView) {
                        setSelectedView(selectedView);
                      }
                    }}
                    selectedItems={selectedViews}
                    rowActionCells={rowActionCells}
                  />
                </div>
                <div style={{ width: '60%' }}>
                  <BasicHeader>Column Selector</BasicHeader>
                  {selectedView.name ? (
                    <FluentTable<IEditableDataGridColumnDef>
                      itemCount={columns.length}
                      height={300}
                      tableFeatureOptions={columnTableFeatureOptions}
                      selectionMode={'multiselect'}
                      selectionKey={'key'}
                      onSelectionChange={(e, data) => {
                        setSelectedView((prevState) => {
                          const newState = cloneDeep(prevState);
                          //Add all the UI columns to the view (If they aren't already on there).
                          columns.forEach((value) => {
                            if (!newState.columns!.find((col) => col?.fieldName === value.key)) {
                              const newColumn: Omit<IEditableDataGridColumn, 'relayId'> = {
                                uuid: UuIdUtils.uuIdGenerator(),
                                fieldName: value.key as string,
                                name: value.name as string,
                                width: value.width,
                                active: !value.viewControllerOptIn,
                              };
                              newState.columns?.push(newColumn as IEditableDataGridColumn);
                            }
                          });

                          //Update existing columns
                          newState.columns?.forEach((value) => {
                            if (!value) return;
                            const isSelected = data.selectedItems.has(value.fieldName as TableRowId);
                            const isMandatory = !!columns.find((column) => column.key === value.fieldName)
                              ?.viewControllerMandatory;
                            if (isMandatory && !isSelected) {
                              dispatchSimpleToast(
                                'Mandatory Column',
                                `${value.name} is a mandatory column and cannot be de-selected`,
                                AlertType.Warning
                              );
                            }

                            value.active = isSelected || isMandatory;
                          });

                          return newState;
                        });
                      }}
                      selectedItems={selectedColumns}
                    />
                  ) : (
                    <h4 style={{ paddingLeft: '10px' }}>
                      Please select a view on the left, or save a new view to get started.
                    </h4>
                  )}
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingLeft: '20px',
                paddingBottom: '3px',
                borderTop: '1px #CCCCCC solid',
              }}
            >
              <Field
                label={'View Name'}
                orientation={'horizontal'}
                hint={'Changing the view name will result in a new view.'}
                className={styles.field}
              >
                <Input
                  data-testid={'manage-view-name'}
                  value={selectedView.name ?? ''}
                  onChange={(ev, data) =>
                    setSelectedView((prevState) => ({
                      ...prevState,
                      id: undefined,
                      concurrencyVersion: undefined,
                      uuid: UuIdUtils.uuIdGenerator(),
                      name: data.value,
                    }))
                  }
                  appearance={'underline'}
                />
              </Field>
              <div style={{ display: 'flex', flexDirection: 'row', paddingRight: '6px' }}>
                <SecondaryButton data-testid={'manage-view-cancel'} onClick={() => setViewControllerOpen(false)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton data-testid={'manage-view-save'} onClick={onClickSave}>
                  Save {selectedView?.id ? '' : 'New '}View
                </PrimaryButton>
              </div>
            </div>
          </FluentProvider>
        </FluentModal>
      ),
      columns: filteredColumns,
      handleColumnResize,
    }),
    [
      action,
      viewControllerOpen,
      viewData?.editableDataGridViews?.count,
      viewTableFeatureOptions,
      selectedViews,
      rowActionCells,
      selectedView.name,
      selectedView?.id,
      columns,
      columnTableFeatureOptions,
      selectedColumns,
      styles.field,
      onClickSave,
      filteredColumns,
      handleColumnResize,
      items,
      setSelectedView,
      dispatchSimpleToast,
    ]
  );
};

export default useEditableDataGridViewController;
